import React, { useEffect, useRef, useState } from 'react';
import { notificationLink } from '../utils/links';
import { useNavigate } from 'react-router-dom';
import {
  useNotifications,
  useMarkRead,
  useMarkAllRead,
  SEVERITY_STYLES,
  KIND_LABELS,
  relTime,
  AppNotification,
} from '../hooks/useNotifications';

// How many rows the dropdown shows before deferring to the full page.
const PANEL_LIMIT = 6;

const NotificationBell: React.FC = () => {
  const [open, setOpen] = useState(false);
  const panelRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  const { data, isLoading } = useNotifications();
  const markRead = useMarkRead();
  const markAllRead = useMarkAllRead();

  const unread = data?.unread_count ?? 0;
  const items = data?.notifications ?? [];

  // Close on outside click and on Escape — a dropdown that traps the page is
  // worse than no dropdown.
  useEffect(() => {
    if (!open) return;
    const onClick = (e: MouseEvent) => {
      if (panelRef.current && !panelRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const openFullPage = () => {
    setOpen(false);
    navigate('/notifications');
  };

  // Each notification opens what it's about (DATA-365, utils/links.ts).
  const onRowClick = (n: AppNotification) => {
    if (!n.read) markRead.mutate(n.id);
    setOpen(false);
    navigate(notificationLink(n));
  };

  return (
    <div className="relative" ref={panelRef}>
      <button
        onClick={() => setOpen((v) => !v)}
        aria-label={
          unread > 0 ? `Notifications, ${unread} unread` : 'Notifications'
        }
        aria-haspopup="true"
        aria-expanded={open}
        title="Notifications"
        className="relative flex h-10 w-10 items-center justify-center rounded-lg border border-line bg-surface text-body hover:bg-canvas focus-visible:outline focus-visible:outline-2 focus-visible:outline-focus"
      >
        <svg
          className="h-5 w-5"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
          <path d="M13.73 21a2 2 0 0 1-3.46 0" />
        </svg>
        {unread > 0 && (
          <span
            className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 rounded-full bg-danger text-on-danger text-[11px] font-semibold leading-[18px] text-center"
            aria-hidden="true"
          >
            {unread > 99 ? '99+' : unread}
          </span>
        )}
      </button>

      {open && (
        <div className="absolute right-0 mt-2 w-96 max-w-[calc(100vw-2rem)] bg-surface rounded-lg shadow-xl border border-line z-50">
          <div className="flex items-center justify-between px-4 py-3 border-b border-line">
            <span className="text-sm font-semibold text-body">
              Notifications
            </span>
            <button
              onClick={() => markAllRead.mutate()}
              disabled={unread === 0 || markAllRead.isPending}
              className="text-xs text-muted hover:text-body disabled:opacity-40 disabled:cursor-not-allowed"
            >
              {markAllRead.isPending ? 'Marking…' : 'Mark all read'}
            </button>
          </div>

          <div className="max-h-96 overflow-y-auto">
            {isLoading && (
              <p className="px-4 py-6 text-sm text-muted">Loading…</p>
            )}

            {!isLoading && items.length === 0 && (
              <div className="px-4 py-8 text-center">
                <p className="text-sm text-muted">Nothing needs attention.</p>
                <p className="text-xs text-muted mt-1">
                  Quota, failing tables and stuck runs show up here.
                </p>
              </div>
            )}

            {items.slice(0, PANEL_LIMIT).map((n) => {
              const s = SEVERITY_STYLES[n.severity] ?? SEVERITY_STYLES.info;
              return (
                <button
                  key={n.id}
                  onClick={() => onRowClick(n)}
                  className={`w-full text-left px-4 py-3 border-b border-line hover:bg-canvas transition-colors ${
                    n.read ? '' : 'bg-canvas'
                  }`}
                >
                  <div className="flex items-start gap-2">
                    <span
                      className={`mt-1.5 h-2 w-2 rounded-full flex-shrink-0 ${
                        n.read ? 'bg-transparent' : s.dot
                      }`}
                      aria-hidden="true"
                    />
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${s.pill}`}>
                          {KIND_LABELS[n.kind] ?? n.kind}
                        </span>
                        {n.occurrences > 1 && (
                          <span className="text-[10px] text-muted">
                            ×{n.occurrences}
                          </span>
                        )}
                        <span className="text-[10px] text-muted ml-auto">
                          {relTime(n.last_seen_at)}
                        </span>
                      </div>
                      <p
                        className={`text-sm mt-1 ${
                          n.read
                            ? 'text-muted'
                            : 'text-body font-medium'
                        }`}
                      >
                        {n.title}
                      </p>
                    </div>
                  </div>
                </button>
              );
            })}
          </div>

          <button
            onClick={openFullPage}
            className="w-full px-4 py-2.5 text-xs text-muted hover:text-body hover:bg-canvas border-t border-line rounded-b-lg transition-colors"
          >
            {items.length > PANEL_LIMIT
              ? `View all ${items.length} notifications`
              : 'View all notifications'}
          </button>
        </div>
      )}
    </div>
  );
};

export default NotificationBell;
